import React from "react";
import { cn } from "@/lib/utils";

// Base Skeleton component
function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("animate-pulse rounded-md bg-muted", className)}
      {...props}
    />
  );
}

// Skeleton Text component - for text lines
interface SkeletonTextProps extends React.HTMLAttributes<HTMLDivElement> {
  lines?: number;
  className?: string;
  lineClassName?: string;
  width?: "full" | "3/4" | "1/2" | "1/3" | "1/4" | string;
  height?: "xs" | "sm" | "md" | "lg" | string;
  spacing?: "tight" | "normal" | "loose";
}

function SkeletonText({
  lines = 1,
  className,
  lineClassName,
  width = "full",
  height = "md",
  spacing = "normal",
  ...props
}: SkeletonTextProps) {
  const heightClasses = {
    xs: "h-3",
    sm: "h-4",
    md: "h-5",
    lg: "h-6",
  };

  const widthClasses = {
    full: "w-full",
    "3/4": "w-3/4",
    "1/2": "w-1/2",
    "1/3": "w-1/3",
    "1/4": "w-1/4",
  };

  const spacingClasses = {
    tight: "space-y-1",
    normal: "space-y-2",
    loose: "space-y-3",
  };

  const heightClass =
    typeof height === "string" &&
    heightClasses[height as keyof typeof heightClasses]
      ? heightClasses[height as keyof typeof heightClasses]
      : height;

  const widthClass =
    typeof width === "string" &&
    widthClasses[width as keyof typeof widthClasses]
      ? widthClasses[width as keyof typeof widthClasses]
      : width;

  if (lines === 1) {
    return (
      <Skeleton
        className={cn(heightClass, widthClass, lineClassName)}
        {...props}
      />
    );
  }

  return (
    <div className={cn(spacingClasses[spacing], className)} {...props}>
      {Array.from({ length: lines }).map((_, index) => {
        // Make the last line shorter for more realistic text appearance
        const isLastLine = index === lines - 1;
        const lineWidth = isLastLine && lines > 1 ? "w-2/3" : widthClass;

        return (
          <Skeleton
            key={index}
            className={cn(heightClass, lineWidth, lineClassName)}
          />
        );
      })}
    </div>
  );
}

// Skeleton Avatar component
interface SkeletonAvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: "xs" | "sm" | "md" | "lg" | "xl" | string;
  shape?: "circle" | "square";
}

function SkeletonAvatar({
  size = "md",
  shape = "circle",
  className,
  ...props
}: SkeletonAvatarProps) {
  const sizeClasses = {
    xs: "w-6 h-6",
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12",
    xl: "w-16 h-16",
  };

  const sizeClass =
    typeof size === "string" && sizeClasses[size as keyof typeof sizeClasses]
      ? sizeClasses[size as keyof typeof sizeClasses]
      : size;

  const shapeClass = shape === "circle" ? "rounded-full" : "rounded-md";

  return (
    <Skeleton className={cn(sizeClass, shapeClass, className)} {...props} />
  );
}

// Skeleton Card component
interface SkeletonCardProps extends React.HTMLAttributes<HTMLDivElement> {
  showAvatar?: boolean;
  avatarSize?: "xs" | "sm" | "md" | "lg" | "xl";
  avatarShape?: "circle" | "square";
  titleWidth?: "full" | "3/4" | "1/2" | "1/3" | "1/4" | string;
  showDescription?: boolean;
  descriptionLines?: number;
  showActions?: boolean;
  actionsCount?: number;
  padding?: "none" | "sm" | "md" | "lg";
}

function SkeletonCard({
  showAvatar = true,
  avatarSize = "md",
  avatarShape = "circle",
  titleWidth = "3/4",
  showDescription = true,
  descriptionLines = 2,
  showActions = false,
  actionsCount = 2,
  padding = "md",
  className,
  ...props
}: SkeletonCardProps) {
  const paddingClasses = {
    none: "",
    sm: "p-3",
    md: "p-4",
    lg: "p-6",
  };

  return (
    <div
      className={cn(
        "bg-white rounded-lg border shadow-sm",
        paddingClasses[padding],
        className,
      )}
      {...props}
    >
      <div className="flex items-start gap-4">
        {showAvatar && <SkeletonAvatar size={avatarSize} shape={avatarShape} />}
        <div className="flex-1 space-y-2">
          <SkeletonText width={titleWidth} height="lg" />
          {showDescription && (
            <SkeletonText lines={descriptionLines} height="sm" width="full" />
          )}
          {showActions && (
            <div className="flex gap-2 pt-2">
              {Array.from({ length: actionsCount }).map((_, index) => (
                <Skeleton key={index} className="h-8 w-20" />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Skeleton Table component
interface SkeletonTableProps extends React.HTMLAttributes<HTMLDivElement> {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  showAvatar?: boolean;
  avatarColumn?: number;
}

function SkeletonTable({
  rows = 5,
  columns = 4,
  showHeader = true,
  showAvatar = false,
  avatarColumn = 0,
  className,
  ...props
}: SkeletonTableProps) {
  return (
    <div
      className={cn(
        "bg-white rounded-lg border shadow-sm overflow-hidden",
        className,
      )}
      {...props}
    >
      {showHeader && (
        <div className="border-b bg-gray-50 p-4">
          <div
            className={`grid gap-4`}
            style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
          >
            {Array.from({ length: columns }).map((_, index) => (
              <SkeletonText key={index} height="sm" width="1/2" />
            ))}
          </div>
        </div>
      )}
      <div className="p-4 space-y-4">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div
            key={rowIndex}
            className={`grid gap-4 items-center`}
            style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
          >
            {Array.from({ length: columns }).map((_, colIndex) => {
              if (showAvatar && colIndex === avatarColumn) {
                return (
                  <div key={colIndex} className="flex items-center gap-3">
                    <SkeletonAvatar size="sm" />
                    <SkeletonText width="3/4" height="sm" />
                  </div>
                );
              }
              return <SkeletonText key={colIndex} height="sm" width="2/3" />;
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// Skeleton List component
interface SkeletonListProps extends React.HTMLAttributes<HTMLDivElement> {
  items?: number;
  showAvatar?: boolean;
  avatarSize?: "xs" | "sm" | "md" | "lg" | "xl";
  avatarShape?: "circle" | "square";
  showDescription?: boolean;
  showBadge?: boolean;
  spacing?: "tight" | "normal" | "loose";
}

function SkeletonList({
  items = 5,
  showAvatar = true,
  avatarSize = "md",
  avatarShape = "circle",
  showDescription = true,
  showBadge = false,
  spacing = "normal",
  className,
  ...props
}: SkeletonListProps) {
  const spacingClasses = {
    tight: "space-y-2",
    normal: "space-y-4",
    loose: "space-y-6",
  };

  return (
    <div className={cn(spacingClasses[spacing], className)} {...props}>
      {Array.from({ length: items }).map((_, index) => (
        <div key={index} className="flex items-center gap-4">
          {showAvatar && (
            <SkeletonAvatar size={avatarSize} shape={avatarShape} />
          )}
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <SkeletonText width="3/4" height="md" />
              {showBadge && <Skeleton className="h-6 w-16" />}
            </div>
            {showDescription && <SkeletonText width="1/2" height="sm" />}
          </div>
        </div>
      ))}
    </div>
  );
}

// Skeleton Form component
interface SkeletonFormProps extends React.HTMLAttributes<HTMLDivElement> {
  fields?: number;
  showLabels?: boolean;
  showButtons?: boolean;
  buttonCount?: number;
  fieldSpacing?: "tight" | "normal" | "loose";
}

function SkeletonForm({
  fields = 4,
  showLabels = true,
  showButtons = true,
  buttonCount = 2,
  fieldSpacing = "normal",
  className,
  ...props
}: SkeletonFormProps) {
  const spacingClasses = {
    tight: "space-y-3",
    normal: "space-y-4",
    loose: "space-y-6",
  };

  return (
    <div className={cn(spacingClasses[fieldSpacing], className)} {...props}>
      {Array.from({ length: fields }).map((_, index) => (
        <div key={index} className="space-y-2">
          {showLabels && <SkeletonText width="1/4" height="sm" />}
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
      {showButtons && (
        <div className="flex gap-3 pt-4">
          {Array.from({ length: buttonCount }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-24" />
          ))}
        </div>
      )}
    </div>
  );
}

// Skeleton Page component - for full page layouts
interface SkeletonPageProps extends React.HTMLAttributes<HTMLDivElement> {
  showHeader?: boolean;
  showSidebar?: boolean;
  showFilters?: boolean;
  contentType?: "list" | "table" | "cards" | "form";
  items?: number;
}

function SkeletonPage({
  showHeader = true,
  showSidebar = false,
  showFilters = false,
  contentType = "list",
  items = 5,
  className,
  ...props
}: SkeletonPageProps) {
  return (
    <div className={cn("min-h-screen flex", className)} {...props}>
      {showSidebar && (
        <div className="w-64 bg-gray-50 p-6">
          <SkeletonList items={6} showDescription={false} avatarSize="sm" />
        </div>
      )}
      <div className="flex-1 flex flex-col">
        {showHeader && (
          <div className="border-b bg-white p-6">
            <SkeletonText width="1/3" height="lg" className="mb-2" />
            <SkeletonText width="1/2" height="sm" />
          </div>
        )}
        <div className="flex-1 p-6">
          {showFilters && (
            <div className="mb-6 flex gap-4">
              <Skeleton className="h-10 w-64" />
              <Skeleton className="h-10 w-32" />
              <Skeleton className="h-10 w-28" />
            </div>
          )}
          {contentType === "list" && <SkeletonList items={items} />}
          {contentType === "table" && <SkeletonTable rows={items} />}
          {contentType === "cards" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: items }).map((_, index) => (
                <SkeletonCard key={index} />
              ))}
            </div>
          )}
          {contentType === "form" && <SkeletonForm />}
        </div>
      </div>
    </div>
  );
}

// Agent Card Skeleton component - matches exact layout of agent cards
interface AgentCardSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  showPhoneStatus?: boolean;
}

function AgentCardSkeleton({
  showPhoneStatus = false,
  className,
  ...props
}: AgentCardSkeletonProps) {
  return (
    <div
      className={cn(
        "group border border-gray-200 rounded-lg overflow-hidden bg-white",
        className,
      )}
      {...props}
    >
      {/* Colored banner skeleton */}
      <Skeleton className="h-20 w-full rounded-none" />

      <div className="p-5 relative">
        {/* Avatar overlapping banner */}
        <div className="absolute -top-12 left-5">
          <div className="relative p-0.5 rounded-full">
            <SkeletonAvatar
              size="xl"
              className="h-32 w-32 border-2 border-white"
            />
          </div>
        </div>

        {/* Content - left aligned */}
        <div className="text-left">
          {/* Avatar and Agent Info Row */}
          <div className="flex items-center gap-6 mb-8">
            {/* Space for avatar (since it's positioned absolute) */}
            <div className="w-32 flex-shrink-0"></div>

            {/* Agent Info Column */}
            <div className="flex-1 min-w-0 space-y-2">
              {/* Main Title (Agent Name) */}
              <SkeletonText width="3/4" height="lg" className="h-8" />

              {/* Subtitle (Title/Description) */}
              <SkeletonText width="1/2" height="md" className="h-6" />

              {/* Phone Number Badge - only for voice agents */}
              {showPhoneStatus && (
                <div className="mt-2">
                  <Skeleton className="h-6 w-24 rounded-full" />
                </div>
              )}
            </div>
          </div>

          {/* Stats grid */}
          <div className="mb-8">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <SkeletonText width="full" height="sm" className="h-4" />
                <SkeletonText width="1/2" height="md" className="h-6" />
              </div>
              <div className="space-y-1">
                <SkeletonText width="full" height="sm" className="h-4" />
                <SkeletonText width="1/2" height="md" className="h-6" />
              </div>
              <div className="space-y-1">
                <SkeletonText width="full" height="sm" className="h-4" />
                <SkeletonText width="1/2" height="md" className="h-6" />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 items-center">
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-9 w-9" />
            <Skeleton className="h-9 w-9" />

            {/* Phone Number Widget - only show if enabled */}
            {showPhoneStatus && (
              <div className="flex-1 ml-2">
                <Skeleton className="h-9 w-full" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export {
  Skeleton,
  SkeletonText,
  SkeletonAvatar,
  SkeletonCard,
  SkeletonTable,
  SkeletonList,
  SkeletonForm,
  SkeletonPage,
  AgentCardSkeleton,
  ConversationChatSkeleton,
  ConversationDetailsSkeleton,
};

// Conversation Chat Skeleton component - for the middle chat panel
interface ConversationChatSkeletonProps
  extends React.HTMLAttributes<HTMLDivElement> {
  messageCount?: number;
}

function ConversationChatSkeleton({
  messageCount = 6,
  className,
  ...props
}: ConversationChatSkeletonProps) {
  return (
    <div className={cn("flex flex-col h-full", className)} {...props}>
      {/* Chat Header Skeleton */}
      <div className="p-4 border-b bg-white flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          {/* Back button skeleton (mobile) */}
          <Skeleton className="md:hidden h-8 w-8" />

          <div className="space-y-2">
            <SkeletonText width="3/4" height="lg" className="h-6" />
            <div className="flex items-center gap-4">
              <SkeletonText width="1/3" height="xs" className="h-3" />
              <SkeletonText width="1/4" height="xs" className="h-3" />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-24" /> {/* Status dropdown */}
          <Skeleton className="h-6 w-16" /> {/* State badge */}
        </div>
      </div>

      {/* Chat Messages Skeleton */}
      <div className="h-full w-full overflow-y-auto p-4 space-y-4">
        {Array.from({ length: messageCount }).map((_, index) => {
          const isUser = index % 3 === 0; // Mix user and agent messages

          if (isUser) {
            // User message - matches: flex justify-end with dark bubble
            return (
              <div key={index} className="flex justify-end">
                <Skeleton
                  className={`px-4 py-3 rounded-[7px] max-w-[80%] ${
                    index % 2 === 0 ? "w-32 h-12" : "w-24 h-8"
                  } bg-slate-700`}
                />
              </div>
            );
          } else {
            // Agent message - matches: flex items-start gap-2 with avatar and gray bubble
            return (
              <div key={index} className="flex items-start gap-2">
                <SkeletonAvatar size="sm" className="h-8 w-8" />
                <Skeleton
                  className={`bg-[#f2f2f2] p-3 rounded-[7px] max-w-[80%] ${
                    index % 2 === 0 ? "w-48 h-16" : "w-32 h-12"
                  }`}
                />
              </div>
            );
          }
        })}
      </div>

      {/* Chat Input Skeleton */}
      <div className="p-4 border-t bg-white">
        <div className="flex gap-2 items-end">
          <Skeleton className="flex-1 h-10" />
          <Skeleton className="h-10 w-10" />
        </div>
      </div>
    </div>
  );
}

// Conversation Details Skeleton component - for the right details panel
interface ConversationDetailsSkeletonProps
  extends React.HTMLAttributes<HTMLDivElement> {
  showFormFields?: boolean;
  formFieldCount?: number;
}

function ConversationDetailsSkeleton({
  showFormFields = true,
  formFieldCount = 3,
  className,
  ...props
}: ConversationDetailsSkeletonProps) {
  return (
    <div className={cn("flex flex-col h-full", className)} {...props}>
      <div className="flex-1 overflow-auto min-h-0">
        {/* User Info Section Skeleton */}
        <div className="p-6 border-b space-y-4">
          <SkeletonText width="2/3" height="lg" className="h-6" />

          <div className="space-y-3">
            {/* Contact details */}
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4" />
              <SkeletonText width="1/2" height="sm" className="h-4" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4" />
              <SkeletonText width="1/3" height="sm" className="h-4" />
            </div>
          </div>

          {/* User type tag */}
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>

        {/* Conversation Insights Section Skeleton */}
        <div className="p-6 border-b space-y-4">
          <SkeletonText width="1/2" height="md" className="h-5" />

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <SkeletonText width="full" height="xs" className="h-3" />
              <SkeletonText width="2/3" height="sm" className="h-4" />
            </div>
            <div className="space-y-2">
              <SkeletonText width="full" height="xs" className="h-3" />
              <SkeletonText width="1/2" height="sm" className="h-4" />
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <SkeletonText width="1/3" height="xs" className="h-3" />
            <div className="flex gap-2">
              <Skeleton className="h-6 w-12 rounded-full" />
              <Skeleton className="h-6 w-16 rounded-full" />
              <Skeleton className="h-6 w-10 rounded-full" />
            </div>
          </div>
        </div>

        {/* Form Fields Section Skeleton */}
        {showFormFields && (
          <div className="p-6 space-y-4">
            <SkeletonText width="1/3" height="md" className="h-5" />

            {Array.from({ length: formFieldCount }).map((_, index) => (
              <div key={index} className="space-y-2">
                <SkeletonText width="1/4" height="sm" className="h-4" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
